import { merge, get, keyBy } from 'lodash';
import storeMixin from '@/js/store/mixins/vuex-store';
import { sortOfferMechanicPresetsByPriority } from '@/js/utils/offer-mechanic-utils';

const getInitialState = () => ({
    offerMechanicPresets: [],
});

/**
 * Inherits from the default store mixin which takes care of all CRUD operations.
 */
const store = {
    namespaced: true,

    /**
     * Default state available:
     * - loading
     */
    state: getInitialState(),

    /**
     * Default getters available:
     * - getOfferMechanicPresetsById
     */
    getters: {
        isPresetsAdminEnabled(state, getters, rootState) {
            return !!get(rootState, 'clientConfig.toggleLogic.enableOfferPresetsAdmin', false);
        },

        adminPresets(state, getters, rootState) {
            const priorityOrder = get(
                rootState,
                'clientConfig.toggleLogic.offerMechanicPresetOrder',
                []
            );
            return sortOfferMechanicPresetsByPriority(state.offerMechanicPresets, priorityOrder, {
                sortRemainingByOrder: getters.isPresetsAdminEnabled,
            });
        },

        sortedOfferMechanicPresets(state, getters) {
            return getters.adminPresets.filter(preset => !preset.isHiddenInPlanner);
        },

        /**
         * Returns offer mechanic presets keyed by template name,
         * sorted by client-configured priority order from feature flags.
         */
        offerMechanicPresetsKeyed(state, getters) {
            return getters.sortedOfferMechanicPresets.reduce((acc, preset) => {
                acc[preset.offerTemplate] = preset.templateStructure;
                return acc;
            }, {});
        },

        presetsByTemplate(state, getters) {
            return keyBy(getters.adminPresets, 'offerTemplate');
        },

        priceTagPresets(state, getters, rootState, rootGetters) {
            return (
                getters.sortedOfferMechanicPresets
                    // Without the admin page only presets with their own tag appear in the gallery.
                    .filter(preset =>
                        getters.isPresetsAdminEnabled
                            ? preset.priceTagId
                            : preset.priceTagId && preset.priceTagId === preset.offerTemplate
                    )
                    .map(preset => ({
                        ...preset,
                        priceTag: rootGetters['priceTags/priceTagsKeyed'][preset.priceTagId],
                    }))
            );
        },

        priceTagPresetsKeyed(state, getters) {
            return keyBy(getters.priceTagPresets, 'offerTemplate');
        },

        priceTagIdsByOfferTemplate(state, getters) {
            return getters.adminPresets.reduce((acc, preset) => {
                acc[preset.offerTemplate] = preset.priceTagId;
                return acc;
            }, {});
        },
    },

    /**
     * Default mutations available:
     * - setLoading
     * - setOfferMechanicPresets
     * - deleteOfferMechanicPresets
     * - updateOfferMechanicPresets
     * - addOfferMechanicPresets
     * - resetState
     */
    mutations: {},

    /**
     * Default actions available:
     * - fetchOfferMechanicPresets
     * - createOfferMechanicPresets
     * - deleteOfferMechanicPresets
     * - updateOfferMechanicPresets
     * - submitForm
     * - handleResponseNotifications
     * - resetState
     */
    actions: {},
};

const mixinParams = {
    resource: 'offer-mechanic-preset',
    getInitialState,
};

export default merge({}, storeMixin(mixinParams), store);
